import { useState } from "react";
import { StatusMessage } from "../ui";
import type { PortfolioTrade } from "../../types/portfolio";
import { cn } from "../../lib/utils";
import type { TutorialSlide } from "../home/HomeTutorialModal";
import { entryFormatter, numberFormatter as floorFormatter } from "./portfolioUtils";
import { TeamCodeBadge } from "../ui/TeamCodeBadge";
import { TEAM_IDENTITY_BADGE_SIZE, TEAM_IDENTITY_NAME_CLASS } from "../ui/teamIdentityScale";
import { getWorldCupFlagCountryCode } from "@sportex100/shared/utils/worldCupFlags";
import { isSoccerTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts";
import { useLocalization } from "../../lib/i18n";

export const historyHowToSlides: TutorialSlide[] = [
  {
    title: "Trades vs Settlements",
    body: `Your **History** shows two types of entries:

**TRADE** (BUY/SELL) - A transaction you executed with another player. You bought or sold shares at an agreed price.

**SETTLEMENT** - An automatic closing when a team is eliminated or wins the championship. The position closes at the team's final floor price.`,
  },
  {
    title: "Quantity (Qty)",
    body: `**Qty** is the total number of shares you traded for that team.

For **trades**, it's the quantity of that specific transaction.

For **settlements**, it's the sum of all your trades for that team (your total position size).`,
  },
  {
    title: "Price (Settlement)",
    body: `For **settlements**, **Price** shows your per-share profit or loss:

**Positive (+)** = You made points per share
**Negative (-)** = You lost points per share

It's calculated as: Total P&L / Quantity

Example: If you sold 40 shares at avg price 38, and the team settled at 10, your per-share profit is **+28**.`,
  },
  {
    title: "P&L (Profit & Loss)",
    body: `The **P&L** column shows your total realized profit or loss for settlements.

**Green (+)** = You made points
**Red (-)** = You lost points

The math is simple: **Qty x Price = P&L**

Example: 40 shares x +28 per share = **+1,120** total profit`,
  },
  {
    title: "Counterparty (Cpty)",
    body: `**Cpty** shows who was on the other side of your trade.

This only appears for **trades** (not settlements). It's the username of the player you traded with.

Settlements don't have a counterparty because they're automatic closings by the system.`,
  },
];

export function HistoryHowToButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="text-foreground-muted p-1 rounded hover:text-brand transition"
      aria-label="How to understand history"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </button>
  );
}

export function useHistoryHowTo() {
  const [showHowTo, setShowHowTo] = useState(false);
  return {
    showHowTo,
    openHowTo: () => setShowHowTo(true),
    closeHowTo: () => setShowHowTo(false),
  };
}

type RecentTradesSectionProps = {
  trades: PortfolioTrade[];
  competitionCode: string;
  quantityFormatter: Intl.NumberFormat;
  numberFormatter: Intl.NumberFormat;
  maxTrades?: number;
};

function formatPnl(pnl: number, formatter: Intl.NumberFormat): string {
  const formatted = formatter.format(Math.abs(pnl));
  return pnl >= 0 ? `+${formatted}` : `-${formatted}`;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDateOnly(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function getCounterpartyDisplayName(trade: PortfolioTrade): string {
  const displayName = trade.counterparty_display_name?.trim();
  if (!trade.counterparty_trader_code && displayName?.toLowerCase() === "deleted trader") {
    return "DELETED";
  }
  return trade.counterparty_trader_code ?? displayName ?? "—";
}

function isVirtualCounterparty(trade: PortfolioTrade): boolean {
  return trade.counterparty_participant_type === "VIRTUAL";
}

function MobileHistoryMetric({
  label,
  value,
  valueClassName,
  align = "left",
  className,
}: {
  label: string;
  value: string;
  valueClassName?: string;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", align === "right" && "text-right", className)}>
      <p className="truncate font-display text-[10px] font-bold uppercase leading-none tracking-[0.08em] text-foreground-faint">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 truncate font-display text-[17px] font-bold leading-none text-foreground tabular-nums",
          valueClassName,
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function RecentTradesSection({
  trades,
  competitionCode,
  quantityFormatter,
  numberFormatter,
  maxTrades,
}: RecentTradesSectionProps) {
  const { t } = useLocalization();
  const [expandedTradeId, setExpandedTradeId] = useState<number | null>(null);
  const displayTrades = maxTrades ? trades.slice(0, maxTrades) : trades;
  const toggleExpandedTrade = (tradeId: number) => {
    setExpandedTradeId((current) => (current === tradeId ? null : tradeId));
  };

  return (
    <div className="p-2">
      {displayTrades.length === 0 ? (
        <StatusMessage>No trades executed yet.</StatusMessage>
      ) : (
        <div>
          <table className="hidden w-full table-fixed">
            <thead className="border-b border-white/10 bg-black/25">
              <tr className="h-10 text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-muted">
                <th className="w-[220px] px-3 text-left">Team</th>
                <th className="w-[90px] px-2 text-left">Type</th>
                <th className="w-[80px] px-2 text-left">Qty</th>
                <th className="w-[90px] px-2 text-left">Price</th>
                <th className="w-[100px] px-2 text-left">Value</th>
                <th className="w-[130px] px-2 text-left">Cpty / P&amp;L</th>
                <th className="w-[150px] px-3 text-right">Executed</th>
              </tr>
            </thead>
            <tbody>
              {displayTrades.map((trade, index) => {
                const isSettlement = trade.trade_type === "SETTLEMENT";
                const sideIsBuy = trade.side === "BUY";
                const teamName =
                  (trade as PortfolioTrade & { team_name?: string }).team_name ?? trade.team_code;
                const worldCupFlagCountryCode =
                  isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(trade.team_code) : null;
                const settlementValue =
                  trade.settlement_floor_price != null
                    ? trade.settlement_floor_price
                    : trade.price + (trade.settlement_pnl ?? 0) / Math.max(trade.quantity, 1);
                const settlementPnl = trade.settlement_pnl ?? (settlementValue - trade.price) * trade.quantity;
                const tradeTotal = trade.price * trade.quantity;

                return (
                  <tr
                    key={`trade-${trade.trade_id}-${trade.executed_at}-${index}`}
                    className="h-12 border-b border-white/[0.08] text-sm transition-colors last:border-b-0 hover:bg-white/[0.03]"
                  >
                    <td className="px-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <TeamCodeBadge
                          code={trade.team_code}
                          size={
                            worldCupFlagCountryCode
                              ? TEAM_IDENTITY_BADGE_SIZE.portfolio.worldCupTableBadge
                              : TEAM_IDENTITY_BADGE_SIZE.portfolio.tableBadge
                          }
                          primaryColor={trade.team_primary_color ?? null}
                          secondaryColor={trade.team_secondary_color ?? null}
                          variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                          flagCountryCode={worldCupFlagCountryCode}
                        />
                        <span className="truncate font-semibold text-foreground">{teamName}</span>
                      </div>
                    </td>
                    <td className="px-2">
                      <span
                        className={cn(
                          "inline-flex min-w-[56px] items-center justify-center rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]",
                          isSettlement
                            ? "bg-brand text-black"
                            : sideIsBuy
                              ? "bg-success/20 text-success"
                              : "bg-danger/20 text-danger",
                        )}
                      >
                        {isSettlement ? "SETTLE" : trade.side}
                      </span>
                    </td>
                    <td className="px-2 text-foreground">{quantityFormatter.format(trade.quantity)}</td>
                    <td className="px-2 text-foreground">
                      {isSettlement && trade.settlement_avg_cost != null
                        ? entryFormatter.format(trade.settlement_avg_cost)
                        : numberFormatter.format(trade.price)}
                    </td>
                    <td className="px-2 text-foreground">
                      {isSettlement
                        ? floorFormatter.format(settlementValue)
                        : numberFormatter.format(tradeTotal)}
                    </td>
                    <td
                      className={cn(
                        "px-2",
                        isSettlement
                          ? settlementPnl >= 0
                            ? "font-semibold text-success"
                            : "font-semibold text-danger"
                          : "text-foreground-muted",
                      )}
                    >
                      {isSettlement ? (
                        formatPnl(settlementPnl, numberFormatter)
                      ) : isVirtualCounterparty(trade) ? (
                        <div className="min-w-0">
                          <p className="truncate">{getCounterpartyDisplayName(trade)}</p>
                          <p
                            className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-brand"
                            title={trade.counterparty_profile_description ?? undefined}
                          >
                            {t("market.participant.virtual")} · {trade.counterparty_profile ?? "AUTOMATED"}
                          </p>
                        </div>
                      ) : (
                        getCounterpartyDisplayName(trade)
                      )}
                    </td>
                    <td className="px-3 text-right text-foreground-muted">{formatDateTime(trade.executed_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="w-full space-y-3">
            {displayTrades.map((trade, index) => {
              const isSettlement = trade.trade_type === "SETTLEMENT";
              const canExpand = !isSettlement;
              const isExpanded = expandedTradeId === trade.trade_id;
              const sideIsBuy = trade.side === "BUY";
              const teamName =
                (trade as PortfolioTrade & { team_name?: string }).team_name ?? trade.team_code;
              const worldCupFlagCountryCode =
                isSoccerTradingCompetition(competitionCode) ? getWorldCupFlagCountryCode(trade.team_code) : null;
              const settlementValue =
                trade.settlement_floor_price != null
                  ? trade.settlement_floor_price
                  : trade.price + (trade.settlement_pnl ?? 0) / Math.max(trade.quantity, 1);
              const settlementPnl = trade.settlement_pnl ?? (settlementValue - trade.price) * trade.quantity;
              const tradeTotal = trade.price * trade.quantity;

              return (
                <article
                  key={`trade-${trade.trade_id}-${trade.executed_at}-${index}`}
                  className={cn(
                    "historyMobileCard historyTabletCard rounded-xl border bg-background-card p-3 shadow-[0_12px_28px_rgba(0,0,0,0.28)] transition-colors md:p-4",
                    isSettlement ? "border-brand" : sideIsBuy ? "border-success" : "border-danger",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (canExpand) toggleExpandedTrade(trade.trade_id);
                    }}
                    disabled={!canExpand}
                    aria-expanded={canExpand ? isExpanded : undefined}
                    className={cn(
                      "grid min-h-11 w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-3 text-left md:grid-cols-[auto_minmax(16rem,1.8fr)_minmax(3.8rem,0.38fr)_minmax(4.4rem,0.48fr)_minmax(5.8rem,0.62fr)_auto] md:items-center md:gap-4",
                      !canExpand && "cursor-default",
                    )}
                  >
                    <div className="self-start">
                      <TeamCodeBadge
                        code={trade.team_code}
                        size={TEAM_IDENTITY_BADGE_SIZE.portfolio.cardBadge}
                        primaryColor={trade.team_primary_color ?? null}
                        secondaryColor={trade.team_secondary_color ?? null}
                        variant={worldCupFlagCountryCode ? "worldCupFlag" : undefined}
                        flagCountryCode={worldCupFlagCountryCode}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className={TEAM_IDENTITY_NAME_CLASS.portfolioCard}>
                        {teamName}
                      </p>
                      <span
                        className={cn(
                          "historyTabletTypePill mt-1 hidden h-[22px] w-fit min-w-12 items-center justify-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em] text-black md:inline-flex",
                          isSettlement ? "bg-brand" : sideIsBuy ? "bg-success" : "bg-danger",
                        )}
                      >
                        {isSettlement
                          ? t("portfolio.trade.settle")
                          : t(sideIsBuy ? "portfolio.filters.buy" : "portfolio.filters.sell").toUpperCase()}
                      </span>
                      <div className="mt-1.5 grid grid-cols-[auto_minmax(2.2rem,0.6fr)_minmax(2.8rem,0.8fr)_minmax(3.2rem,0.9fr)_auto] items-end gap-x-2 gap-y-1 md:hidden">
                        <span
                          className={cn(
                            "historyMobileTypePill inline-flex h-[22px] min-w-12 items-center justify-center self-center rounded px-2 font-display text-[9px] font-bold uppercase tracking-[0.06em] text-black",
                            isSettlement ? "bg-brand" : sideIsBuy ? "bg-success" : "bg-danger",
                          )}
                        >
                          {isSettlement
                            ? t("portfolio.trade.settle")
                            : t(sideIsBuy ? "portfolio.filters.buy" : "portfolio.filters.sell").toUpperCase()}
                        </span>
                        {isSettlement ? (
                          <>
                            <MobileHistoryMetric
                              label={t("portfolio.metric.date")}
                              value={formatDateOnly(trade.executed_at)}
                            />
                            <MobileHistoryMetric
                              label={t("portfolio.metric.floor")}
                              value={floorFormatter.format(settlementValue)}
                            />
                            <MobileHistoryMetric
                              align="right"
                              label={t("portfolio.metric.pnl")}
                              value={formatPnl(settlementPnl, numberFormatter)}
                              valueClassName={settlementPnl >= 0 ? "text-success" : "text-danger"}
                            />
                          </>
                        ) : (
                          <>
                            <MobileHistoryMetric
                              label={t("portfolio.metric.qty")}
                              value={quantityFormatter.format(trade.quantity)}
                            />
                            <MobileHistoryMetric
                              label={t("portfolio.metric.price")}
                              value={entryFormatter.format(trade.price)}
                            />
                            <MobileHistoryMetric
                              align="right"
                              label={t("portfolio.metric.total")}
                              value={numberFormatter.format(tradeTotal)}
                            />
                          </>
                        )}
                        {canExpand ? (
                          <span className="pb-1 font-display text-lg leading-none text-foreground-muted md:flex md:min-h-11 md:w-11 md:items-center md:justify-center md:justify-self-end md:pb-0" aria-hidden="true">
                            {isExpanded ? "▴" : "▾"}
                          </span>
                        ) : (
                          <span className="md:min-h-11 md:w-11" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                    {isSettlement ? (
                      <>
                        <MobileHistoryMetric
                          className="hidden md:block"
                          label={t("portfolio.metric.date")}
                          value={formatDateOnly(trade.executed_at)}
                        />
                        <MobileHistoryMetric
                          className="hidden md:block"
                          label={t("portfolio.metric.floor")}
                          value={floorFormatter.format(settlementValue)}
                        />
                        <MobileHistoryMetric
                          className="hidden md:block"
                          align="right"
                          label={t("portfolio.metric.pnl")}
                          value={formatPnl(settlementPnl, numberFormatter)}
                          valueClassName={settlementPnl >= 0 ? "text-success" : "text-danger"}
                        />
                      </>
                    ) : (
                      <>
                        <MobileHistoryMetric
                          className="hidden md:block"
                          label={t("portfolio.metric.qty")}
                          value={quantityFormatter.format(trade.quantity)}
                        />
                        <MobileHistoryMetric
                          className="hidden md:block"
                          label={t("portfolio.metric.price")}
                          value={entryFormatter.format(trade.price)}
                        />
                        <MobileHistoryMetric
                          className="hidden md:block"
                          align="right"
                          label={t("portfolio.metric.total")}
                          value={numberFormatter.format(tradeTotal)}
                        />
                      </>
                    )}
                    {canExpand ? (
                      <span className="hidden font-display text-lg leading-none text-foreground-muted md:flex md:min-h-11 md:w-11 md:items-center md:justify-center md:justify-self-end" aria-hidden="true">
                        {isExpanded ? "▴" : "▾"}
                      </span>
                    ) : (
                      <span className="hidden md:block md:min-h-11 md:w-11" aria-hidden="true" />
                    )}
                  </button>

                  {canExpand && isExpanded ? (
                    <div className="historyDetailsPanelMobile mt-3 grid grid-cols-2 gap-3 border-t border-white/[0.08] pt-3">
                      <div className="min-w-0">
                        <p className="font-display text-[12px] font-bold uppercase tracking-[0.12em] text-foreground-faint">
                          {t("portfolio.metric.date")}
                        </p>
                        <p className="historyDetailValue mt-1 truncate font-display text-[17px] font-bold text-foreground">
                          {formatDateOnly(trade.executed_at)}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="font-display text-[12px] font-bold uppercase tracking-[0.12em] text-foreground-faint">
                          {t("portfolio.metric.counterparty")}
                        </p>
                        <p className="historyDetailValue mt-1 truncate font-display text-[17px] font-bold text-foreground">
                          {getCounterpartyDisplayName(trade)}
                        </p>
                        {isVirtualCounterparty(trade) ? (
                          <p
                            className="mt-1 truncate text-xs font-semibold uppercase tracking-[0.08em] text-brand"
                            title={trade.counterparty_profile_description ?? undefined}
                          >
                            {t("market.participant.virtual")} · {trade.counterparty_profile ?? "AUTOMATED"}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
